/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { ICookieSetOptions } from '@msdyn365-commerce/core-internal';
import { getCartState, ICartState } from '@msdyn365-commerce/global-state';
import { SalesOrder } from '@msdyn365-commerce/retail-proxy';
import CryptoJS from 'crypto-js';
import * as React from 'react';
import { ICommisionJunctionData } from './commision-junction.data';
import { ICommisionJunctionProps, pageType } from './commision-junction.props.autogenerated';

interface ISiteTagItem {
    itemId: string | undefined;
    unitPrice: number | undefined;
    quantity: number | undefined;
    discount?: number | undefined;
}

interface ISiteTag {
    enterpriseId: string | undefined;
    pageType: pageType | undefined;
    userId: string;
    emailHash: string;
    cartSubtotal: number | undefined;
    items: ISiteTagItem[];
}

interface IConversionTag {
    actionTrackerId: number;
    amount: number;
    cjeventOrder: string;
    coupon?: string;
    currency: string;
    enterpriseId: string | undefined;
    orderId: string;
    pageType: pageType | undefined;
    userId: string;
    emailHash: string;
    items: ISiteTagItem[];
}

export interface ICommisionJunctionViewProps extends ICommisionJunctionExtentedProps<ICommisionJunctionData> {
    isCJEnabled: boolean;
    siteTag: ISiteTag;
    conversionTag?: IConversionTag;
}

export interface ICommisionJunctionExtentedProps<T> extends ICommisionJunctionProps<ICommisionJunctionData> {
    salesOrder?: SalesOrder;
}

/**
 * Age Gate state.
 */
export interface ICommisionJunctionState {
    cartData?: ICartState;
}
/**
 *
 * CommisionJunction component
 * @extends {React.PureComponent<ICommisionJunctionProps<ICommisionJunctionData>, ICommisionJunctionState>}
 */
class CommisionJunction extends React.PureComponent<ICommisionJunctionExtentedProps<ICommisionJunctionData>, ICommisionJunctionState> {
    constructor(props: ICommisionJunctionExtentedProps<ICommisionJunctionData>) {
        super(props);
        this.state = { cartData: undefined };
    }

    public async componentDidMount() {
        if (this.props.config.pageType !== pageType.conversionConfirmation) {
            const currentCartState = await getCartState(this.props.context.actionContext);

            if (currentCartState !== undefined) {
                this.setState({
                    cartData: currentCartState
                });
            }
        }
    }

    private readonly _isConsentGiven: boolean = this.props.context.request.cookies.isConsentGiven();
    public render(): JSX.Element | null {
        const cookieName = this.props.config.cookieName || 'cje';
        const { cjevent = '' } = this.props.context.request.query || {};
        if (cjevent && cjevent !== null && cjevent !== '') {
            const cookieOptions: ICookieSetOptions = {
                secure: true,
                sameSite: 'none'
            };
            cookieOptions.maxAge = 395 * 24 * 60 * 60;
            this.props.context.request.cookies.set(cookieName, cjevent, cookieOptions);
        }

        const isCJEnabled = this._isConsentGiven && this.props.config.isCJEnabled;

        let siteTag: ISiteTag | null = null;
        let conversionTag: IConversionTag | null = null;
        if (isCJEnabled) {
            if (this.props.config.pageType !== pageType.conversionConfirmation) {
                siteTag = this._setSiteTag();
            } else {
                conversionTag = this._setConversionTag();
            }
        }
        const viewProps = {
            ...this.props,
            isConsentGiven: this._isConsentGiven,
            isCJEnabled: this._isConsentGiven && this.props.config.isCJEnabled,
            siteTag: siteTag,
            conversionTag: conversionTag
        };
        return this.props.renderView(viewProps);
    }

    private _setSiteTag(): ISiteTag {
        const enterpriseId = this.props.config.enterpriseId;
        let emailAddressHash;
        if (this.props.context.request.user.emailAddress !== undefined && this.props.context.request.user.emailAddress !== null) {
            const email = this.props.context.request.user.emailAddress.toLocaleLowerCase().trim();
            emailAddressHash = CryptoJS.SHA256(email).toString(CryptoJS.enc.Hex);
        }
        const items: ISiteTagItem[] = [];
        let subTotalAmount: number = 0;
        subTotalAmount = (this.state.cartData && this.state.cartData.cart.SubtotalAmount) || 0;
        const cartLines = this.state.cartData && this.state.cartData.cart.CartLines;
        if (cartLines && cartLines?.length > 0) {
            cartLines.map(cartline => {
                const item = {
                    itemId: cartline.ItemId,
                    unitPrice: cartline.Price,
                    quantity: cartline.Quantity,
                    discount: cartline.DiscountAmount
                };

                items.push(item);
            });
        }

        return {
            enterpriseId: enterpriseId,
            pageType: this.props.config.pageType,
            userId: this.props.context.request.user.customerAccountNumber || '',
            emailHash: emailAddressHash || '',
            cartSubtotal: subTotalAmount,
            items: items
        };
    }

    private _setConversionTag(): IConversionTag | null {
        const { salesId = '', transactionId = '' } = this.props.context.request.query || {};
        const channelReferenceId = this.props?.salesOrder?.ChannelReferenceId || '';
        const enterpriseId = this.props.config.enterpriseId;
        const cjeCookieValue = this.props.context.request.cookies.get<string>(this.props.config.cookieName || 'cje').value;
        const items: ISiteTagItem[] = [];
        const salesOrder = this.props.salesOrder;
        const totalAmount: number | undefined = salesOrder && salesOrder.NetAmountWithoutTax;
        const currencyCode: string | undefined = salesOrder && salesOrder.CurrencyCode;
        const orderId: string | undefined = salesId || channelReferenceId || transactionId;
        let emailAddressHash;
        if (this.props.context.request.user.emailAddress !== undefined && this.props.context.request.user.emailAddress !== null) {
            const email = this.props.context.request.user.emailAddress.toLocaleLowerCase().trim();
            emailAddressHash = CryptoJS.SHA256(email).toString(CryptoJS.enc.Hex);
        }
        const coupons: string[] = [];

        const salesLines = salesOrder?.SalesLines;
        if (salesLines && salesLines?.length > 0) {
            salesLines.map(salesLine => {
                const item: ISiteTagItem = {
                    itemId: salesLine.ItemId,
                    unitPrice: salesLine.Price,
                    quantity: salesLine.Quantity
                };
                salesLine.DiscountLines!.map(discount => {
                    // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
                    if (discount.DiscountCode && discount.DiscountCode !== '') {
                        coupons.push(discount.DiscountCode);
                    }
                    item.discount = (item.discount || 0) + (discount.EffectiveAmount || 0);
                });
                items.push(item);
            });
        }

        if (currencyCode !== undefined && enterpriseId !== undefined && orderId !== undefined && totalAmount !== undefined) {
            const couponsUnq = [...new Set(coupons)];
            return {
                actionTrackerId: 430475,
                amount: totalAmount,
                cjeventOrder: cjeCookieValue || '',
                coupon: couponsUnq.length > 0 ? `${couponsUnq.join(',')}` : '',
                currency: currencyCode,
                enterpriseId: enterpriseId,
                orderId: orderId,
                pageType: this.props.config.pageType,
                userId: this.props.context.request.user.customerAccountNumber || '',
                emailHash: emailAddressHash || '',
                items: items
            };
        }

        return null;
    }
}

export default CommisionJunction;
